/**
 * Utility functions for handling archaeological era dates in Megalith Maps
 * Provides consistent handling of date formatting, parsing, and comparison
 * with special handling for "present" values
 */

// Define a special constant for "present" to use in comparisons
// Use a symbol that can't be confused with a year
const PRESENT_TOKEN = Symbol('present');

/**
 * Parses an era string into a numeric value for comparison
 * - BCE dates are converted to negative numbers
 * - CE dates are kept as positive numbers
 * - "present" is maintained as a special token for comparison
 *
 * @param {string} eraStr - Era string in format "XXXX BCE", "XXXX CE", "present", or with a trailing "?" (e.g., "XXXX BCE?")
 * @returns {{value: number|Symbol, isApproximate: boolean}|null} - Object with numeric value (or PRESENT_TOKEN) and approximation status, or null if invalid format.
 */
export const parseEra = (eraStrInput) => {
  if (!eraStrInput) return null;

  let eraStr = String(eraStrInput);
  const isApproximate = eraStr.endsWith('?');
  if (isApproximate) {
    eraStr = eraStr.slice(0, -1); // Remove the '?' for parsing
  }
  
  if (eraStr.toLowerCase() === 'present') {
    return { value: PRESENT_TOKEN, isApproximate };
  }
  
  const match = eraStr.match(/^(\d+)\s+(BCE|CE)$/i); // Case-insensitive match
  if (!match) return null;
  
  const year = parseInt(match[1], 10);
  const era = match[2].toUpperCase();
  
  if (isNaN(year)) return null; 
  
  return { value: era === 'BCE' ? -year : year, isApproximate };
};

/**
 * Formats a numeric year value as an era string
 * - Negative numbers become "XXXX BCE"
 * - Positive numbers become "XXXX CE"
 * - Current year can optionally be formatted as "present"
 *
 * @param {number} yearValue - Numeric year value
 * @param {boolean} usePresent - Whether to use "present" for current year
 * @returns {string} - Formatted era string, or empty string if input is invalid
 */
export const formatEra = (yearValue, usePresent = false) => {
  if (yearValue === null || yearValue === undefined || isNaN(Number(yearValue))) return '';
  
  const currentYear = new Date().getUTCFullYear();
  const numericYear = Number(yearValue);
  
  // If using "present" and the year is current year, return "present"
  if (usePresent && numericYear === currentYear) {
    return 'present';
  }
  
  if (numericYear < 0) {
    return `${Math.abs(numericYear)} BCE`;
  } else if (numericYear === 0) {
    // Decide how to handle year 0 if necessary, common convention avoids it
    // For simplicity, treating as 1 CE or 1 BCE might be needed depending on context
    // Or return an empty string/error indicator
    return '1 CE'; // Example: Treat 0 as 1 CE, adjust as needed
  } else {
    return `${numericYear} CE`;
  }
};

/**
 * Compares two era strings chronologically
 * Handles both BCE/CE formats and "present"
 *
 * @param {string} era1 - First era string
 * @param {string} era2 - Second era string
 * @returns {number} - Negative if era1 < era2, positive if era1 > era2, 0 if equal or invalid input
 */
export const compareEras = (era1, era2) => {
  const parsed1Result = parseEra(era1);
  const parsed2Result = parseEra(era2);
  
  if (parsed1Result === null || parsed2Result === null) return 0;

  const val1 = parsed1Result.value;
  const val2 = parsed2Result.value;
  
  // Special handling for "present" token
  if (val1 === PRESENT_TOKEN && val2 === PRESENT_TOKEN) return 0;
  if (val1 === PRESENT_TOKEN) return 1; // Present is always greater
  if (val2 === PRESENT_TOKEN) return -1; // Present is always greater
  
  // Normal numeric comparison for years
  return val1 - val2;
};

/**
 * Converts an era string for display,
 * with special handling to convert "present" to current year display when needed
 *
 * @param {string} eraStr - The era string to convert
 * @param {boolean} showPresentAsText - Whether to show "present" as-is (true) or convert to year (false)
 * @returns {string} - The display string, or empty string if input is invalid
 */
export const displayEra = (eraStr, showPresentAsText = true) => {
  if (!eraStr) return '';
  
  const originalEraStr = String(eraStr); 

  if (originalEraStr === '?') {
    return '?';
  }
  
  const isApproximate = originalEraStr.endsWith('?');
  const baseEraStr = isApproximate ? originalEraStr.slice(0, -1) : originalEraStr;
  const eraLower = baseEraStr.toLowerCase();
  
  if (eraLower === 'present') {
    const presentDisplay = showPresentAsText ? 'Present' : `${new Date().getUTCFullYear()} CE`;
    if (isApproximate && baseEraStr.toLowerCase() === 'present' && showPresentAsText) {
        return originalEraStr; 
    }
    return presentDisplay;
  }
  
  const match = baseEraStr.match(/^(\d+)\s+(BCE|CE)$/i);
  if (!match) return ''; 
  
  return originalEraStr; 
};

/**
 * Validates if a string is a properly formatted era ("XXXX BCE", "XXXX CE", or "present")
 *
 * @param {string} eraStr - The era string to validate
 * @returns {boolean} - Whether the era string is valid
 */
export const isValidEra = (eraStr) => {
  if (!eraStr) return false;
  
  let validationStr = String(eraStr); 
  if (validationStr.endsWith('?')) {
    validationStr = validationStr.slice(0, -1); 
  }

  const eraLower = validationStr.toLowerCase();
  if (eraLower === 'present') return true;
  
  const match = eraLower.match(/^(\d+)\s+(bce|ce)$/); 
  return !!match;
};
